// the SHELL theme layer: the house standard's skinnable rule.
//
//   structure is shared and fixed, look and feel is a swappable layer.
//   mechanism: kidgames docs/shell-theme-tokens.md
//
// this is NOT the game-art skin layer (skins.js: tubes vs bolts vs blocks, the
// CONTAINER and the MOTION verb). the two are orthogonal on purpose: a theme repaints
// the chrome and the board surround, a skin changes what the pieces ARE. you can run
// any skin under any theme.
//
// a theme is nothing but token VALUES. no structure, no class names, no markup. that
// is what makes it swappable, and what the theme-swap proof checks.

import { readSlot, writeSlot } from '../storage.ts'

const KEY = 'sortit:theme'

export const THEME_TOKENS = [
  '--surface', '--surface-raised', '--surface-sunk', '--surface-complete',
  '--ink', '--ink-dim', '--ink-on-accent',
  '--accent', '--accent-dim', '--line', '--warn',
  '--font-ui', '--font-display', '--font-mono',
  '--radius', '--radius-pill',
  '--shadow', '--shadow-pressed', '--shadow-low', '--shadow-lift', '--shadow-ring', '--shadow-sheet',
  '--mark-heart',
] as const

export type ShellTheme = {
  key: string
  title: string
  swatch: string[]
  tokens: Record<typeof THEME_TOKENS[number], string>
}

// the tokens no theme repaints: the type, the shapes (radius and shadow geometry,
// which read the theme's own --line and --accent), and the maker mark's heart (the
// signature on the work, the same in every theme). app.css :root and its
// [data-theme] blocks mirror every theme here, and verify-shell.mjs holds them equal.
const FIXED = {
  '--font-ui': 'ui-rounded, "Arial Rounded MT Bold", "Nunito", system-ui, sans-serif',
  '--font-display': 'var(--font-ui)',
  '--font-mono': 'ui-monospace, monospace',
  '--radius': '1rem',
  '--radius-pill': '999px',
  '--shadow': '0 .3rem 0 var(--line)',
  '--shadow-pressed': '0 .05rem 0 var(--line)',
  '--shadow-low': '0 .25rem 0 var(--line)',
  '--shadow-lift': '0 .35rem 0 var(--line)',
  '--shadow-ring': '0 0 0 .3rem var(--accent)',
  '--shadow-sheet': '0 -1rem 3rem color-mix(in srgb, var(--line) 18%, transparent)',
  '--mark-heart': '#e0746a',
}

export const SHELL_THEMES: ShellTheme[] = [
  {
    key: 'daylight',
    title: 'Daylight',
    swatch: ['#FFF6E5', '#FFB03A', '#3D3230'],
    tokens: {
      '--surface': '#FFF6E5',
      '--surface-raised': '#FFFFFF',
      '--surface-sunk': '#FFEED2',
      '--surface-complete': '#C6E8C2',
      '--ink': '#3D3230',
      '--ink-dim': '#78675D',
      '--ink-on-accent': '#3D3230',
      '--accent': '#FFB03A',
      '--accent-dim': '#E89B27',
      '--line': '#3D3230',
      '--warn': '#C0392B',
      ...FIXED,
    },
  },
  {
    key: 'dusk',
    title: 'Dusk',
    swatch: ['#241F2E', '#FFB03A', '#F2ECE2'],
    tokens: {
      '--surface': '#241F2E',
      '--surface-raised': '#332B42',
      '--surface-sunk': '#1B1724',
      '--surface-complete': '#26472D',
      '--ink': '#F2ECE2',
      '--ink-dim': '#B3A8BF',
      '--ink-on-accent': '#241F2E',
      '--accent': '#FFB03A',
      '--accent-dim': '#D8912A',
      '--line': '#F2ECE2',
      '--warn': '#E08A8A',
      ...FIXED,
    },
  },
  {
    key: 'bubblegum',
    title: 'Bubblegum',
    swatch: ['#FFF0F6', '#FF74A8', '#4A2740'],
    tokens: {
      '--surface': '#FFF0F6',
      '--surface-raised': '#FFFFFF',
      '--surface-sunk': '#FFE0EC',
      '--surface-complete': '#C6E8C2',
      '--ink': '#4A2740',
      '--ink-dim': '#795669',
      '--ink-on-accent': '#4A2740',
      '--accent': '#FF74A8',
      '--accent-dim': '#E85C90',
      '--line': '#4A2740',
      '--warn': '#C0392B',
      ...FIXED,
    },
  },
]

export function themeByKey(key: string | null) {
  return SHELL_THEMES.find(t => t.key === key) ?? SHELL_THEMES[0]
}

export function loadTheme() {
  return themeByKey(readSlot(KEY))
}

export function saveTheme(key: string) {
  return writeSlot(KEY, key)
}

// paint by writing the token VALUES onto the root. nothing else moves: same DOM, same
// class names, same structure, which is exactly the compliance bar.
export function applyTheme(theme: ShellTheme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  for (const [token, value] of Object.entries(theme.tokens)) root.style.setProperty(token, value)
  root.dataset.theme = theme.key
}
